/**
 * One proposal, or one contract: the document, and the terms underneath it.
 *
 * The `kind` prop is the only thing that changes between the two — same
 * editor, same tables, same PDF export, different wording and a different
 * primary action (Convert for a proposal, Approve for a contract). A proposal
 * that has already been converted, like an approved contract, is frozen: the
 * `frozen` flag below covers both.
 *
 * Two views of the same record. **Edit** is the working surface — prose blocks
 * you type into, and the commercial rows underneath them. **Preview** is the
 * client's copy, which is also what prints.
 *
 * Prose and money are edited in different places on purpose. The blocks carry
 * the writing; the segments panel carries the investment lines and the draw
 * schedules, and the document's tables render from those rows rather than from
 * anything typed into them. That is the whole reason approval can promise that
 * what the client signed is what the project bills.
 *
 * Saving writes both halves in one act: segments first, because a payment
 * schedule block names the segment it renders and a brand-new segment has no
 * id until it has been saved.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { forwardRef, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { EMPTY_DOC, type ProseDoc, RichText, RichTextView } from "../RichText";
import { api, apiErrorMessage, fmtAmount, rateFromPercent, rateToPercent, unwrap } from "../api";
import {
  type BrandPalette,
  type BrandTypography,
  type CompanyIdentity,
  EMPTY_IDENTITY,
  brandPalette,
  brandTypography,
  documentName,
  useBrandFonts,
  useCompanyIdentity,
} from "../brand";
import { DOCUMENT_COPY, documentFacts, joinPartNames, scheduleRowsFor } from "../document-content";
import { downloadProposalDocx } from "../docx";
import {
  CONTENT_WIDTH_PX,
  PAGE_HEIGHT_PX,
  PAGE_MARGIN_PX,
  PAGE_WIDTH_PX,
  paginate,
  samePages,
} from "../paged";
import { downloadProposalPdf } from "../pdf";
import { Button, ErrorNote, Field, Loading, Modal, Panel, Stat, input } from "../ui";
import { StatusPill } from "./Contracts";
import {
  AcceptanceBlock,
  InvestmentTable,
  ScheduleTable,
  type SegmentDraft,
  SegmentEditor,
  emptySegment,
  investmentRows,
  newKey,
  resolveDraft,
  safeItemAmount,
} from "./contract-parts";

type SectionKind = "prose" | "investment" | "payment_schedule" | "acceptance";

export interface SectionDraft {
  key: number;
  kind: SectionKind;
  title: string;
  body: ProseDoc | null;
  /** For `payment_schedule`: the *draft* key(s) of the segment(s) it renders,
   *  so a block can point at a segment that hasn't been saved yet. Resolved
   *  to real ids at save time. More than one merges those segments' draws
   *  into a single table — see `combineSchedules` in `@dantria/core`. */
  segmentKeys: number[];
}

const KIND_LABELS: Record<SectionKind, string> = {
  prose: "Text",
  investment: "Investment table",
  payment_schedule: "Payment schedule",
  acceptance: "Signature block",
};

/** Copy that differs between the offer and the binding document it converts
 *  into — everything else in this file treats the two identically. What the
 *  *document* says comes from `DOCUMENT_COPY`, because the Word export needs
 *  the same words and has no page to put a back link on. */
const KIND_COPY = {
  proposal: { backLabel: "← Proposals", ...DOCUMENT_COPY.proposal },
  contract: { backLabel: "← Contracts", ...DOCUMENT_COPY.contract },
} as const;

/**
 * What a downloaded copy is called, for either format.
 *
 * Shared rather than derived at each call site because the two downloads of
 * one document turning up in a folder under two different names is exactly the
 * confusion a client does not need. Windows and macOS each refuse part of what
 * a title may legitimately contain, and a save the OS rejects looks to the
 * operator like an export that failed.
 */
export function exportName(contract: { number: string; title: string }): string {
  const cleaned = `${contract.number} - ${contract.title}`.replace(/[\\/:*?"<>|]+/g, " ").trim();
  return cleaned || "proposal";
}

export function ContractDetail({
  slug,
  contractId,
  kind,
  role,
  onBack,
  onOpenProject,
  onOpenEstimate,
  onOpenContract,
}: {
  slug: string;
  contractId: string;
  kind: "proposal" | "contract";
  role: string;
  onBack: () => void;
  onOpenProject: (id: string) => void;
  onOpenEstimate?: (id: string) => void;
  /** Cross-links to the proposal a contract came from, or the contract a
   *  proposal was converted to. Optional only so this component doesn't need
   *  a stub in tests that never render those links. */
  onOpenContract?: (id: string, kind: "proposal" | "contract") => void;
}) {
  const qc = useQueryClient();
  const canManage = role === "manager" || role === "admin" || role === "owner";
  const copy = KIND_COPY[kind];

  const [preview, setPreview] = useState(false);
  const docRef = useRef<HTMLElement>(null);
  // Where the sheets under the document begin. Measured from the laid-out DOM
  // rather than derived from the content, because only the browser knows how
  // tall a paragraph in the company's own typeface turned out to be.
  const [pageTops, setPageTops] = useState<number[]>([0]);
  // Print always shows the client's copy first (see `DocumentView`'s doc
  // comment), so a click while still editing switches to Preview and this
  // flag tells the effect below to export as soon as that copy is mounted.
  const [pendingPdf, setPendingPdf] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  // Word needs no such dance: `../docx.ts` builds from the rows in state, not
  // from the mounted document, so it exports whichever tab happens to be open.
  const [exportingDocx, setExportingDocx] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [approving, setApproving] = useState(false);
  // Separate from `error`: a failure here happens while the approve dialog is
  // still open, and `error` renders in the page body — behind the dialog's
  // backdrop, where nobody would ever see it. This one renders inside the
  // dialog itself.
  const [approveError, setApproveError] = useState<string | null>(null);
  const [converting, setConverting] = useState(false);
  const [convertError, setConvertError] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [sections, setSections] = useState<SectionDraft[]>([]);
  const [segments, setSegments] = useState<SegmentDraft[]>([]);
  const [header, setHeader] = useState<Record<string, string>>({});

  const q = useQuery({
    queryKey: ["contract", slug, contractId],
    queryFn: async () =>
      unwrap(await api.api.c[":slug"].contracts[":id"].$get({ param: { slug, id: contractId } })),
  });

  /**
   * Fetched here rather than passed down from the shell: the letterhead needs
   * the logo, and the logo is the one piece of company identity the API keeps
   * out of the context every request carries. Branding saved in Settings shares
   * this cache key, so an open proposal picks it up without a reload.
   */
  const identityQuery = useCompanyIdentity(slug);
  const identity: CompanyIdentity = identityQuery.data?.company ?? EMPTY_IDENTITY;
  const palette = brandPalette(identity.brandColor);
  const typography = useMemo(() => brandTypography(identity), [identity]);
  const fontsReady = useBrandFonts(typography);

  // Load the server's copy into the draft once per fetch. Guarded on `dirty`
  // so a background refetch cannot discard something half-typed.
  const loaded = q.data;
  useEffect(() => {
    if (!loaded || dirty) return;

    const segs: SegmentDraft[] = loaded.segments.map((seg) => ({
      key: newKey(),
      id: seg.id,
      name: seg.name,
      projectCode: seg.projectCode ?? "",
      retainagePercent: seg.retainagePct === "0.000000" ? "" : rateToPercent(seg.retainagePct),
      startDate: seg.startDate ?? "",
      targetEndDate: seg.targetEndDate ?? "",
      projectId: seg.projectId,
      items: seg.items.map((i) => ({
        key: newKey(),
        itemNo: i.itemNo ?? "",
        description: i.description,
        amount: i.amount,
        estimateLineId: i.estimateLineId,
      })),
      schedule: seg.schedule.map((r) => ({
        key: newKey(),
        name: r.name,
        basis: r.basis,
        percent: r.percent ? rateToPercent(r.percent) : "",
        amount: r.amount ?? "",
        plannedDate: r.plannedDate ?? "",
      })),
    }));
    const keyById = new Map(segs.map((s) => [s.id, s.key]));

    setSegments(segs);
    setSections(
      loaded.sections.map((sec) => ({
        key: newKey(),
        kind: sec.kind as SectionKind,
        title: sec.title ?? "",
        body: (sec.body as ProseDoc | null) ?? null,
        segmentKeys: sec.segmentIds
          .map((id) => keyById.get(id))
          .filter((k): k is number => k !== undefined),
      })),
    );
    setHeader({
      title: loaded.contract.title,
      clientContactName: loaded.contract.clientContactName ?? "",
      clientPhone: loaded.contract.clientPhone ?? "",
      clientEmail: loaded.contract.clientEmail ?? "",
      projectLocation: loaded.contract.projectLocation ?? "",
      projectType: loaded.contract.projectType ?? "",
      preparedBy: loaded.contract.preparedBy ?? "",
      tagline: loaded.contract.tagline ?? "",
      validThrough: loaded.contract.validThrough ?? "",
    });
  }, [loaded, dirty]);

  /**
   * Re-lays the document onto sheets whenever anything that could move a line
   * changes. A layout effect, not a passive one, so the whitespace is in place
   * before the browser paints — a frame of the unpaginated column would read as
   * a flash of the old, continuous document.
   *
   * The observer covers what no dependency list can see: a brand face arriving,
   * the logo decoding, the window narrowing. `paginate` clears its own
   * whitespace before measuring, so the pass it triggers on its own mutation
   * settles rather than compounding.
   */
  // The drafts are in the dependency list on purpose, closed over or not: an
  // edit that leaves the column exactly as tall as it was still moves the
  // breaks, and the observer below would never hear about it — it watches the
  // box, not what is written inside it.
  // biome-ignore lint/correctness/useExhaustiveDependencies: re-measure on content, not only on size
  useLayoutEffect(() => {
    if (!preview) return;
    const root = docRef.current;
    if (!root) return;

    let frame = 0;
    const run = () => {
      const next = paginate(root);
      setPageTops((cur) => (samePages(cur, next) ? cur : next));
    };
    run();

    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(run);
    });
    observer.observe(root);
    return () => {
      observer.disconnect();
      cancelAnimationFrame(frame);
    };
  }, [preview, sections, segments, fontsReady]);

  useEffect(() => {
    // Also waits on the company and its faces: exporting before branding has
    // arrived produces a PDF with no logo, the fallback color and the fallback
    // typeface — a document that looks finished and is wrong.
    if (
      !pendingPdf ||
      !preview ||
      !docRef.current ||
      !loaded ||
      !identityQuery.isSuccess ||
      !fontsReady
    ) {
      return;
    }
    setPendingPdf(false);
    setExportingPdf(true);
    const doc = docRef.current;
    downloadProposalPdf(doc, `${exportName(loaded.contract)}.pdf`, () => paginate(doc))
      .catch((err) => {
        console.error("PDF export failed", err);
        const message = err instanceof Error ? err.message : String(err);
        setError(`Couldn't create the PDF: ${message}`);
      })
      .finally(() => setExportingPdf(false));
  }, [pendingPdf, preview, loaded, identityQuery.isSuccess, fontsReady]);

  function refresh() {
    return qc.invalidateQueries({ queryKey: ["contract", slug, contractId] });
  }

  const save = useMutation({
    mutationFn: async () => {
      // Segments first: a payment-schedule block names a segment, and a new
      // one has no id to name until it has been written.
      const savedSegments = await unwrap(
        await api.api.c[":slug"].contracts[":id"].segments.$put({
          param: { slug, id: contractId },
          json: {
            segments: segments.map((seg) => ({
              id: seg.id,
              name: seg.name.trim() || "Untitled",
              projectCode: seg.projectCode.trim() || null,
              retainagePct: seg.retainagePercent
                ? rateFromPercent(seg.retainagePercent)
                : undefined,
              startDate: seg.startDate || null,
              targetEndDate: seg.targetEndDate || null,
              items: seg.items
                .filter((i) => i.description.trim() || Number(i.amount) > 0)
                .map((i) => ({
                  itemNo: i.itemNo.trim() || null,
                  description: i.description.trim() || "—",
                  quantity: "1",
                  amount: safeItemAmount(i.amount) ?? "0.00",
                  estimateLineId: i.estimateLineId,
                })),
              schedule: seg.schedule
                .filter((r) => r.name.trim())
                .map((r) => ({
                  name: r.name.trim(),
                  basis: r.basis,
                  percent: r.basis === "percent" ? rateFromPercent(r.percent || "0") : null,
                  amount: r.basis === "amount" ? r.amount : null,
                  plannedDate: r.plannedDate || null,
                })),
            })),
          },
        }),
      );

      // The API returns segments in the order they were sent, so index maps a
      // draft key onto the id it was just given.
      const idByKey = new Map<number, string>();
      segments.forEach((seg, i) => {
        const saved = savedSegments.segments[i];
        if (saved) idByKey.set(seg.key, saved.id);
      });

      await api.api.c[":slug"].contracts[":id"].sections.$put({
        param: { slug, id: contractId },
        json: {
          sections: sections.map((sec) => ({
            kind: sec.kind,
            title: sec.title.trim() || null,
            body: sec.kind === "prose" ? (sec.body ?? EMPTY_DOC) : null,
            segmentIds:
              sec.kind === "payment_schedule"
                ? sec.segmentKeys.map((key) => idByKey.get(key)).filter((id): id is string => !!id)
                : [],
          })),
        },
      });

      await api.api.c[":slug"].contracts[":id"].$patch({
        param: { slug, id: contractId },
        json: {
          title: header.title,
          clientContactName: header.clientContactName,
          clientPhone: header.clientPhone,
          clientEmail: header.clientEmail,
          projectLocation: header.projectLocation,
          projectType: header.projectType,
          preparedBy: header.preparedBy,
          tagline: header.tagline,
          validThrough: header.validThrough || null,
        },
      });
    },
    onSuccess: async () => {
      setError(null);
      setDirty(false);
      await refresh();
    },
    onError: (err) => setError(apiErrorMessage(err, "Couldn't save this proposal.")),
  });

  const setStatus = useMutation({
    mutationFn: async (status: string) =>
      unwrap(
        await api.api.c[":slug"].contracts[":id"].status.$post({
          param: { slug, id: contractId },
          json: { status: status as "draft" },
        }),
      ),
    onSuccess: async () => {
      setError(null);
      await refresh();
    },
    onError: (err) => setError(apiErrorMessage(err, "Couldn't change status.")),
  });

  const approve = useMutation({
    mutationFn: async (body: { acceptedByName: string; acceptedOn: string }) =>
      unwrap(
        await api.api.c[":slug"].contracts[":id"].approve.$post({
          param: { slug, id: contractId },
          json: {
            acceptedByName: body.acceptedByName || null,
            acceptedOn: body.acceptedOn || null,
          },
        }),
      ),
    onSuccess: async () => {
      setApproving(false);
      setError(null);
      setApproveError(null);
      await Promise.all([refresh(), qc.invalidateQueries({ queryKey: ["projects", slug] })]);
    },
    onError: (err) => setApproveError(apiErrorMessage(err, "Couldn't approve this contract.")),
  });

  const convert = useMutation({
    mutationFn: async (body: { allowUnaccepted: boolean }) =>
      unwrap(
        await api.api.c[":slug"].contracts[":id"].convert.$post({
          param: { slug, id: contractId },
          json: { allowUnaccepted: body.allowUnaccepted },
        }),
      ),
    onSuccess: async (result) => {
      setConverting(false);
      setError(null);
      setConvertError(null);
      await Promise.all([refresh(), qc.invalidateQueries({ queryKey: ["contracts", slug] })]);
      onOpenContract?.(result.contract.id, "contract");
    },
    onError: (err) =>
      setConvertError(apiErrorMessage(err, "Couldn't convert this proposal to a contract.")),
  });

  const touch = () => setDirty(true);

  const resolvedByKey = useMemo(
    () => new Map(segments.map((seg) => [seg.key, resolveDraft(seg)])),
    [segments],
  );
  const grandTotal = useMemo(
    () =>
      segments
        .reduce((acc, seg) => acc + Number(resolvedByKey.get(seg.key)?.total ?? 0), 0)
        .toFixed(2),
    [segments, resolvedByKey],
  );

  if (q.isPending) return <Loading />;
  if (q.isError) return <ErrorNote>{(q.error as Error).message}</ErrorNote>;

  const contract = q.data.contract;
  const approved = contract.status === "approved";
  // A proposal converted to a contract has its own copy of everything now —
  // frozen against edits the same way an approved contract is, and for the
  // same reason: a document already acted on should not keep changing under
  // the thing built from it.
  const convertedTo = kind === "proposal" ? q.data.convertedTo : null;
  const sourceProposal = kind === "contract" ? q.data.sourceProposal : null;
  const frozen = approved || !!convertedTo;
  const editable = canManage && !frozen && !preview;
  const madeProjects = q.data.segments.filter((s) => s.projectId);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Button variant="ghost" onClick={onBack}>
          {copy.backLabel}
        </Button>
        <div className="flex flex-wrap items-center gap-2 print:hidden">
          <Button variant="ghost" onClick={() => setPreview(!preview)}>
            {preview ? "Edit" : "Preview"}
          </Button>
          <Button
            variant="ghost"
            disabled={exportingPdf}
            onClick={() => {
              setPreview(true);
              setPendingPdf(true);
            }}
          >
            {exportingPdf ? "Preparing…" : "Print"}
          </Button>
          <Button
            variant="ghost"
            disabled={exportingDocx || !identityQuery.isSuccess}
            onClick={() => {
              setExportingDocx(true);
              downloadProposalDocx(
                {
                  kind,
                  contract,
                  clientName: q.data.client?.name ?? "",
                  identity,
                  palette,
                  sections,
                  segments,
                  resolvedByKey,
                },
                `${exportName(contract)}.docx`,
              )
                .catch((err) => {
                  console.error("Word export failed", err);
                  const message = err instanceof Error ? err.message : String(err);
                  setError(`Couldn't create the Word file: ${message}`);
                })
                .finally(() => setExportingDocx(false));
            }}
          >
            {exportingDocx ? "Preparing…" : "Word"}
          </Button>
          {canManage && !frozen && (
            <>
              <Button onClick={() => save.mutate()} disabled={!dirty || save.isPending}>
                {save.isPending ? "Saving…" : dirty ? "Save" : "Saved"}
              </Button>
              {contract.status === "draft" && (
                <Button variant="ghost" onClick={() => setStatus.mutate("issued")}>
                  Mark issued
                </Button>
              )}
              {kind === "proposal" && contract.status === "issued" && (
                <Button variant="ghost" onClick={() => setStatus.mutate("accepted")}>
                  Mark accepted
                </Button>
              )}
              {kind === "proposal" ? (
                contract.status !== "draft" && (
                  <Button
                    onClick={() => {
                      setConvertError(null);
                      setConverting(true);
                    }}
                    disabled={dirty}
                    title={dirty ? "Save your changes first" : undefined}
                  >
                    Convert to contract
                  </Button>
                )
              ) : (
                <Button
                  onClick={() => {
                    setApproveError(null);
                    setApproving(true);
                  }}
                  disabled={dirty}
                  title={dirty ? "Save your changes first" : undefined}
                >
                  Approve
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      {error && <ErrorNote>{error}</ErrorNote>}

      {convertedTo && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
          <p>
            Converted to contract{" "}
            <button
              type="button"
              className="font-medium underline underline-offset-2 hover:text-blue-900"
              onClick={() => onOpenContract?.(convertedTo.id, "contract")}
            >
              {convertedTo.number}
            </button>
            . Edit the contract from here on — this proposal is frozen.
          </p>
        </div>
      )}

      {approved && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
          <p className="font-medium">
            Approved{contract.acceptedByName ? ` by ${contract.acceptedByName}` : ""} — this
            contract is live.
          </p>
          <p className="mt-1">
            {madeProjects.length === 1 ? "It created " : "It created "}
            {madeProjects.map((seg, i) => (
              <span key={seg.id}>
                {i > 0 && ", "}
                <button
                  type="button"
                  className="underline underline-offset-2 hover:text-emerald-900"
                  onClick={() => seg.projectId && onOpenProject(seg.projectId)}
                >
                  {seg.name}
                </button>
              </span>
            ))}
            {madeProjects.length === 1 ? " as a project." : " as projects."} Changes from here go
            through change orders.
          </p>
        </div>
      )}

      <Panel
        title={`${contract.number} — ${contract.title}`}
        action={<StatusPill status={contract.status} />}
      >
        {(q.data.estimate || sourceProposal) && (
          <div className="mb-4 flex flex-col gap-1 text-sm text-neutral-500">
            {q.data.estimate && (
              <p>
                Priced from estimate{" "}
                {onOpenEstimate ? (
                  <button
                    type="button"
                    onClick={() => onOpenEstimate(q.data.estimate!.id)}
                    className="underline underline-offset-2 hover:text-neutral-900"
                  >
                    {q.data.estimate.name}
                  </button>
                ) : (
                  q.data.estimate.name
                )}
              </p>
            )}
            {sourceProposal && (
              <p>
                Converted from proposal{" "}
                {onOpenContract ? (
                  <button
                    type="button"
                    onClick={() => onOpenContract(sourceProposal.id, "proposal")}
                    className="underline underline-offset-2 hover:text-neutral-900"
                  >
                    {sourceProposal.number}
                  </button>
                ) : (
                  sourceProposal.number
                )}
              </p>
            )}
          </div>
        )}
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <Stat label="Total" value={fmtAmount(grandTotal, contract.currency)} emphasis />
          <Stat
            label="Parts"
            value={String(segments.length)}
            hint={kind === "contract" ? "Each becomes a project" : undefined}
          />
          <Stat label="Client" value={q.data.client?.name ?? "—"} />
          <Stat label="Valid through" value={contract.validThrough ?? "—"} />
        </div>
      </Panel>

      {preview ? (
        <PagedFrame pageTops={pageTops}>
          <DocumentView
            ref={docRef}
            kind={kind}
            contract={contract}
            clientName={q.data.client?.name ?? ""}
            identity={identity}
            palette={palette}
            typography={typography}
            sections={sections}
            segments={segments}
            resolvedByKey={resolvedByKey}
          />
        </PagedFrame>
      ) : (
        <>
          <Panel title="Header">
            <div className="grid gap-3 rounded-xl border border-neutral-200 bg-white p-5 shadow-sm sm:grid-cols-3">
              {(
                [
                  ["title", "Title"],
                  ["clientContactName", "Prepared for"],
                  ["projectType", "Project type"],
                  ["projectLocation", "Location"],
                  ["clientPhone", "Phone"],
                  ["clientEmail", "Email"],
                  ["preparedBy", "Prepared by"],
                  ["tagline", "Tagline"],
                ] as const
              ).map(([key, label]) => (
                <Field key={key} label={label}>
                  <input
                    className={input}
                    value={header[key] ?? ""}
                    disabled={!editable}
                    onChange={(e) => {
                      setHeader({ ...header, [key]: e.target.value });
                      touch();
                    }}
                  />
                </Field>
              ))}
              <Field label="Valid through">
                <input
                  type="date"
                  className={input}
                  value={header.validThrough ?? ""}
                  disabled={!editable}
                  onChange={(e) => {
                    setHeader({ ...header, validThrough: e.target.value });
                    touch();
                  }}
                />
              </Field>
            </div>
          </Panel>

          <Panel
            title="What the client is buying"
            action={
              editable && (
                <Button
                  onClick={() => {
                    setSegments([...segments, emptySegment()]);
                    touch();
                  }}
                >
                  + Add a part
                </Button>
              )
            }
          >
            <p className="mb-4 text-sm text-neutral-500">
              {kind === "proposal"
                ? "Each part carries its own payment schedule below it. Converting this proposal " +
                  "to a contract copies these parts across, ready to become projects on approval."
                : "Each part becomes its own project when this contract is approved, with the " +
                  "payment schedule below it as that project's billing milestones."}
            </p>
            <div className="flex flex-col gap-4">
              {segments.map((seg) => (
                <SegmentEditor
                  key={seg.key}
                  segment={seg}
                  currency={contract.currency}
                  removable={editable && !seg.projectId && segments.length > 1}
                  onChange={(next) => {
                    setSegments(segments.map((s) => (s.key === seg.key ? next : s)));
                    touch();
                  }}
                  onRemove={() => {
                    setSegments(segments.filter((s) => s.key !== seg.key));
                    // Drop this part from any schedule block that named it —
                    // including a merged one, which just loses that part and
                    // keeps rendering the rest — and drop the block itself
                    // only once nothing is left for it to show.
                    setSections(
                      sections
                        .map((sec) =>
                          sec.kind === "payment_schedule"
                            ? { ...sec, segmentKeys: sec.segmentKeys.filter((k) => k !== seg.key) }
                            : sec,
                        )
                        .filter(
                          (sec) => sec.kind !== "payment_schedule" || sec.segmentKeys.length > 0,
                        ),
                    );
                    touch();
                  }}
                />
              ))}
            </div>
          </Panel>

          <Panel title="The document">
            <p className="mb-4 text-sm text-neutral-500">
              Text blocks are yours to write. The investment and payment-schedule blocks render the
              rows above, so their numbers always match what approval will create.
            </p>
            <div className="flex flex-col gap-3">
              {sections.map((sec, i) => (
                <SectionBlock
                  key={sec.key}
                  section={sec}
                  editable={editable}
                  segments={segments}
                  currency={contract.currency}
                  palette={palette}
                  resolvedByKey={resolvedByKey}
                  first={i === 0}
                  last={i === sections.length - 1}
                  onChange={(next) => {
                    setSections(sections.map((s) => (s.key === sec.key ? next : s)));
                    touch();
                  }}
                  // A part already picked on another schedule block isn't
                  // offered here at all — the block that has it is the only
                  // place it can be unchecked from, so unchecking it there is
                  // what makes it available to pick again, here or anywhere
                  // else.
                  unavailableSegmentKeys={
                    new Set(
                      sections
                        .filter((s) => s.kind === "payment_schedule" && s.key !== sec.key)
                        .flatMap((s) => s.segmentKeys),
                    )
                  }
                  onMove={(dir) => {
                    const j = i + dir;
                    if (j < 0 || j >= sections.length) return;
                    const next = [...sections];
                    [next[i], next[j]] = [next[j]!, next[i]!];
                    setSections(next);
                    touch();
                  }}
                  onRemove={() => {
                    setSections(sections.filter((s) => s.key !== sec.key));
                    touch();
                  }}
                />
              ))}
            </div>

            {editable && (
              <div className="mt-4 flex flex-wrap gap-2">
                {(Object.keys(KIND_LABELS) as SectionKind[]).map((kind) => (
                  <Button
                    key={kind}
                    variant="ghost"
                    onClick={() => {
                      setSections([
                        ...sections,
                        {
                          key: newKey(),
                          kind,
                          title: kind === "prose" ? "" : KIND_LABELS[kind],
                          body: kind === "prose" ? EMPTY_DOC : null,
                          segmentKeys:
                            kind === "payment_schedule" && segments[0] ? [segments[0].key] : [],
                        },
                      ]);
                      touch();
                    }}
                  >
                    + {KIND_LABELS[kind]}
                  </Button>
                ))}
              </div>
            )}
          </Panel>
        </>
      )}

      {approving && (
        <ApproveModal
          currency={contract.currency}
          total={grandTotal}
          segments={segments}
          onCancel={() => {
            setApproving(false);
            setApproveError(null);
          }}
          onConfirm={(body) => approve.mutate(body)}
          pending={approve.isPending}
          error={approveError}
        />
      )}

      {converting && (
        <ConvertModal
          number={contract.number}
          accepted={contract.status === "accepted"}
          onCancel={() => {
            setConverting(false);
            setConvertError(null);
          }}
          onConfirm={(allowUnaccepted) => convert.mutate({ allowUnaccepted })}
          pending={convert.isPending}
          error={convertError}
        />
      )}
    </div>
  );
}

function SectionBlock({
  section,
  editable,
  segments,
  currency,
  palette,
  resolvedByKey,
  first,
  last,
  onChange,
  unavailableSegmentKeys,
  onMove,
  onRemove,
}: {
  section: SectionDraft;
  editable: boolean;
  segments: SegmentDraft[];
  currency: string;
  /** The editor shows the client's tables in the client's colors — the whole
   *  point of these blocks is that they are what will be sent. */
  palette: BrandPalette;
  resolvedByKey: Map<number, ReturnType<typeof resolveDraft>>;
  first: boolean;
  last: boolean;
  onChange: (next: SectionDraft) => void;
  /** `payment_schedule` only: parts already picked on some other schedule
   *  block — left off this block's checkbox list entirely, since a part's
   *  draws belong in exactly one table. Unchecking it from wherever it lives
   *  now is what makes it choosable again. */
  unavailableSegmentKeys: ReadonlySet<number>;
  onMove: (dir: -1 | 1) => void;
  onRemove: () => void;
}) {
  const { combined } = scheduleRowsFor(section.segmentKeys, segments, resolvedByKey);

  return (
    <div className="rounded-xl border border-neutral-200 bg-white p-4 shadow-sm">
      <div className="mb-2 flex items-center gap-2">
        <span className="rounded bg-neutral-100 px-2 py-0.5 text-[10px] uppercase tracking-wide text-neutral-500">
          {KIND_LABELS[section.kind]}
        </span>
        <input
          className={`${input} flex-1 py-1 font-medium`}
          value={section.title}
          disabled={!editable}
          placeholder="Heading (optional)"
          onChange={(e) => onChange({ ...section, title: e.target.value })}
        />
        {editable && (
          <div className="flex items-center print:hidden">
            <Button variant="ghost" onClick={() => onMove(-1)} disabled={first}>
              ↑
            </Button>
            <Button variant="ghost" onClick={() => onMove(1)} disabled={last}>
              ↓
            </Button>
            <Button variant="ghost" onClick={onRemove}>
              ×
            </Button>
          </div>
        )}
      </div>

      {section.kind === "payment_schedule" && (
        <div className="mb-3 flex flex-wrap items-center gap-x-3 gap-y-1.5">
          <span className="text-xs text-neutral-500">
            {segments.length > 1
              ? "Parts on this schedule — pick more than one to merge:"
              : "Part:"}
          </span>
          {segments
            .filter((s) => !unavailableSegmentKeys.has(s.key))
            .map((s) => (
              <label key={s.key} className="flex items-center gap-1.5 text-sm text-neutral-700">
                <input
                  type="checkbox"
                  checked={section.segmentKeys.includes(s.key)}
                  disabled={!editable}
                  onChange={(e) => {
                    const segmentKeys = e.target.checked
                      ? [...section.segmentKeys, s.key]
                      : section.segmentKeys.filter((k) => k !== s.key);
                    onChange({ ...section, segmentKeys });
                  }}
                />
                {s.name || "Untitled"}
              </label>
            ))}
          {segments.some((s) => unavailableSegmentKeys.has(s.key)) && (
            <span className="text-xs text-neutral-400">
              (already on another schedule block below)
            </span>
          )}
        </div>
      )}

      {section.kind === "prose" ? (
        <RichText
          value={section.body ?? EMPTY_DOC}
          editable={editable}
          onChange={(body) => onChange({ ...section, body })}
        />
      ) : section.kind === "investment" ? (
        <InvestmentTable {...investmentRows(segments)} currency={currency} palette={palette} />
      ) : section.kind === "payment_schedule" ? (
        combined && combined.rows.length > 0 ? (
          <ScheduleTable rows={combined.rows} currency={currency} palette={palette} />
        ) : (
          <p className="text-sm text-neutral-500">Pick which part(s) this schedule is for.</p>
        )
      ) : (
        <AcceptanceBlock />
      )}
    </div>
  );
}

/**
 * The sheets the document sits on.
 *
 * They are drawn behind the text rather than around it: the document is one
 * continuous column that `paginate` has pushed into page-shaped runs, and a
 * sheet is a white rectangle positioned under each of those runs. Wrapping each
 * page's content in its own box would mean rendering the document once per
 * page — with a TipTap view per prose block each time — and would put a
 * container between the export and the text it rasterizes.
 *
 * Nothing here is captured by the PDF: the export rasterizes the column, not
 * this frame, so the shadows and the grey behind the paper stay on screen where
 * they belong.
 */
export function PagedFrame({
  pageTops,
  children,
}: { pageTops: number[]; children: React.ReactNode }) {
  const lastTop = pageTops[pageTops.length - 1] ?? 0;
  return (
    <div className="w-full overflow-x-auto rounded-xl bg-neutral-200 p-6">
      <div
        className="relative mx-auto"
        style={{ width: PAGE_WIDTH_PX, minHeight: lastTop + PAGE_HEIGHT_PX }}
      >
        <div aria-hidden className="absolute inset-0">
          {pageTops.map((top, i) => (
            <div
              // Sheets have no identity of their own — they are positions, and
              // the position is the only thing that changes between renders.
              // biome-ignore lint/suspicious/noArrayIndexKey: page number is the identity
              key={i}
              className="absolute left-0 bg-white shadow-md"
              style={{ top, width: PAGE_WIDTH_PX, height: PAGE_HEIGHT_PX }}
            />
          ))}
        </div>
        <div
          className="relative"
          style={{
            marginLeft: PAGE_MARGIN_PX,
            paddingTop: PAGE_MARGIN_PX,
            width: CONTENT_WIDTH_PX,
          }}
        >
          {children}
        </div>
      </div>
    </div>
  );
}

/**
 * The client's copy — no chrome, no controls, laid out on the sheets above and
 * rasterized page by page by the Print button (see `../pdf.ts`).
 *
 * `data-block` marks the runs `../paged.ts` is allowed to break between, and it
 * shifts them by writing `style.marginTop`. So nothing carrying that attribute
 * may take a `style` prop from React: the next render would rewrite the
 * attribute wholesale and the page would silently lose its break. That is why
 * the letterhead, whose rule is drawn in the tenant's color, is wrapped rather
 * than marked.
 */
export const DocumentView = forwardRef<
  HTMLElement,
  {
    kind: "proposal" | "contract";
    contract: {
      number: string;
      title: string;
      currency: string;
      tagline: string | null;
      projectType: string | null;
      projectLocation: string | null;
      preparedBy: string | null;
      clientContactName: string | null;
      clientPhone: string | null;
      clientEmail: string | null;
      issuedOn: string | null;
      validThrough: string | null;
      acceptedByName: string | null;
      acceptedAt: string | null;
    };
    clientName: string;
    identity: CompanyIdentity;
    palette: BrandPalette;
    typography: BrandTypography;
    sections: SectionDraft[];
    segments: SegmentDraft[];
    resolvedByKey: Map<number, ReturnType<typeof resolveDraft>>;
  }
>(function DocumentView(
  { kind, contract, clientName, identity, palette, typography, sections, segments, resolvedByKey },
  ref,
) {
  const signatory = documentName(identity);
  const copy = KIND_COPY[kind];

  /**
   * Body text takes `fontFamily` directly, because `--font-sans` is resolved on
   * `<body>` and children inherit the computed value rather than the variable.
   * Headings take the variable, because that is what they are written against —
   * including the `h1`/`h2` inside prose blocks, which `RichTextView` styles
   * with `--font-display` and which therefore follow along with no changes of
   * their own. Both are omitted entirely when no face is set, leaving the app's
   * stylesheet to say what it always said.
   */
  const fontStyle: React.CSSProperties = {
    ...(typography.body ? { fontFamily: typography.body } : {}),
    ...(typography.display ? { "--font-display": typography.display } : {}),
  } as React.CSSProperties;
  const facts = documentFacts(kind, contract, clientName);

  return (
    // No border, no rounding, no shadow and no padding of its own: this is the
    // text on the page, and the page is drawn behind it by `PagedFrame`. A
    // frame here would rasterize into the PDF as a rule down both edges of
    // every sheet, cut wherever the page happened to end.
    <article ref={ref} style={fontStyle} className="w-full bg-transparent">
      {/* Inline styles, not classes: these colors come from the tenant at
          runtime, so Tailwind has nothing to compile, and the PDF export
          rasterizes this DOM with a renderer that reads plain hex and little
          else (see ../pdf.ts). */}
      <div data-block="letterhead">
        <header
          className="mb-8 pb-6 text-center"
          style={{ borderBottom: `2px solid ${palette.base}` }}
        >
          {identity.logoUrl && (
            // Not decorative: the logo is the company's own wordmark, so its
            // name is the only text that describes it.
            <img
              src={identity.logoUrl}
              alt={signatory}
              className="mx-auto mb-2 h-30 w-auto max-w-72 object-contain"
            />
          )}
          <h1 className="font-[family-name:--font-display] text-3xl tracking-tight text-neutral-900">
            {copy.heading}
          </h1>
          {contract.tagline && <p className="mt-2 text-sm text-neutral-500">{contract.tagline}</p>}
          {(identity.phone || identity.email) && (
            <p className="mt-2 text-xs text-neutral-500">
              {[identity.phone, identity.email].filter(Boolean).join(" · ")}
            </p>
          )}
        </header>
      </div>

      {/* Two columns unconditionally: the column is a fixed 7.5in of paper, so a
          viewport breakpoint here would print a phone's layout onto letter. */}
      <dl data-block="facts" className="mb-10 grid grid-cols-2 gap-x-8 gap-y-3">
        {facts.map(([label, value]) => (
          <div key={label} className="border-b border-neutral-100 pb-1.5">
            <dt className="text-[10px] uppercase tracking-wide" style={{ color: palette.ink }}>
              {label}
            </dt>
            <dd className="text-sm text-neutral-800">{value}</dd>
          </div>
        ))}
      </dl>

      {sections.map((sec) => {
        const { picked, combined } = scheduleRowsFor(sec.segmentKeys, segments, resolvedByKey);
        return (
          <section key={sec.key} data-block={`section-${sec.key}`} className="mb-8">
            {sec.title && (
              <h2
                // A solid border color, never an opacity modifier: that compiles
                // to `color-mix()`, which html2canvas-pro can no more parse than
                // plain html2canvas can parse oklch.
                className="mb-3 border-b pb-1.5 font-[family-name:--font-display] text-lg tracking-tight"
                style={{ borderColor: palette.base, color: palette.ink }}
              >
                {sec.title}
                {sec.kind === "payment_schedule" && picked.length > 0
                  ? ` for ${joinPartNames(picked.map((s) => s.name))}`
                  : ""}
              </h2>
            )}
            {sec.kind === "prose" ? (
              <RichTextView value={sec.body ?? EMPTY_DOC} />
            ) : sec.kind === "investment" ? (
              <InvestmentTable
                {...investmentRows(segments)}
                currency={contract.currency}
                palette={palette}
              />
            ) : sec.kind === "payment_schedule" ? (
              combined &&
              combined.rows.length > 0 && (
                <ScheduleTable
                  rows={combined.rows}
                  currency={contract.currency}
                  palette={palette}
                />
              )
            ) : (
              <AcceptanceBlock
                acceptedByName={contract.acceptedByName}
                acceptedAt={contract.acceptedAt}
                companyName={signatory || undefined}
              />
            )}
          </section>
        );
      })}

      {/* The entity and its EIN, once, at the foot — a client filing this needs
          them, and they have no place competing with the letterhead. */}
      {(identity.legalName || identity.ein) && (
        <footer
          data-block="footer"
          className="mt-10 border-t border-neutral-200 pt-4 text-center text-[10px] text-neutral-500"
        >
          {[signatory, identity.ein && `EIN ${identity.ein}`].filter(Boolean).join(" · ")}
        </footer>
      )}
    </article>
  );
});

function ApproveModal({
  currency,
  total,
  segments,
  onCancel,
  onConfirm,
  pending,
  error,
}: {
  currency: string;
  total: string;
  segments: SegmentDraft[];
  onCancel: () => void;
  onConfirm: (body: { acceptedByName: string; acceptedOn: string }) => void;
  pending: boolean;
  error: string | null;
}) {
  const [name, setName] = useState("");
  const [on, setOn] = useState(new Date().toISOString().slice(0, 10));
  const pendingSegments = segments.filter((s) => !s.projectId);

  return (
    <Modal title="Approve this contract" onClose={onCancel}>
      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}
      <p className="text-sm text-neutral-600">
        This creates{" "}
        <strong>
          {pendingSegments.length} {pendingSegments.length === 1 ? "project" : "projects"}
        </strong>{" "}
        worth {fmtAmount(total, currency)} in total, with their payment schedules as billing
        milestones and any contractor prices chosen on the estimate behind this proposal as
        commitments.
      </p>
      <ul className="my-4 flex flex-col gap-1 text-sm">
        {pendingSegments.map((seg) => (
          <li key={seg.key} className="flex justify-between border-b border-neutral-100 pb-1">
            <span>{seg.name || "Untitled"}</span>
            <span className="tabular text-neutral-500">
              {fmtAmount(resolveDraft(seg)?.total ?? "0.00", currency)}
            </span>
          </li>
        ))}
      </ul>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Signed by" hint="The client's name on the signature line">
          <input className={input} value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label="Signed on">
          <input type="date" className={input} value={on} onChange={(e) => setOn(e.target.value)} />
        </Field>
      </div>
      <p className="mt-3 text-xs text-neutral-500">
        Once approved, the paperwork is frozen — changes go through change orders on the projects it
        creates.
      </p>
      <div className="mt-5 flex justify-end gap-2">
        <Button variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          onClick={() => onConfirm({ acceptedByName: name.trim(), acceptedOn: on })}
          disabled={pending}
        >
          {pending ? "Approving…" : "Approve and create projects"}
        </Button>
      </div>
    </Modal>
  );
}

/**
 * Turns an accepted proposal into a contract: a new document, copied from this
 * one's current segments, items and schedule rows, carrying the company's
 * contract wording instead of the proposal's. Nothing here is undoable from
 * the UI — the proposal is frozen the moment the contract exists.
 */
function ConvertModal({
  number,
  accepted,
  onCancel,
  onConfirm,
  pending,
  error,
}: {
  number: string;
  accepted: boolean;
  onCancel: () => void;
  onConfirm: (allowUnaccepted: boolean) => void;
  pending: boolean;
  error: string | null;
}) {
  const [allowUnaccepted, setAllowUnaccepted] = useState(false);

  return (
    <Modal title="Convert to a contract" onClose={onCancel}>
      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}
      <p className="text-sm text-neutral-600">
        This creates a new contract from proposal <strong>{number}</strong>'s current segments,
        items and payment schedule — copied, not linked, so the two can be edited independently from
        here. The contract carries the company's own contract wording, not this proposal's text.
        This proposal is frozen once the contract exists.
      </p>
      {!accepted && (
        <label className="mt-4 flex items-start gap-2 text-sm text-neutral-600">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={allowUnaccepted}
            onChange={(e) => setAllowUnaccepted(e.target.checked)}
          />
          <span>
            This proposal hasn't been marked accepted yet. Convert it to a contract anyway.
          </span>
        </label>
      )}
      <div className="mt-5 flex justify-end gap-2">
        <Button variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          onClick={() => onConfirm(allowUnaccepted)}
          disabled={pending || (!accepted && !allowUnaccepted)}
        >
          {pending ? "Converting…" : "Convert to contract"}
        </Button>
      </div>
    </Modal>
  );
}
